import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Carousel } from 'react-bootstrap';
import Row from '../ResponsiveRow/row';
import Styles from './StaticLineTable.module.scss';
import { trackNewDatalayerEvent } from '../../utils/AnalyticsUtils';
import { BUNDLEPRODUCT, GAEVENT, TABCLICK, TOGGLECLICK } from '../../Analytics/AnalyticsConstants';
import StaticProductContainer from './StaticProductContainer';
import { StaticLinetableSchema } from './StaticProductDatatypes';
import Cookies from 'js-cookie';
import { getSessionStorage, isStandaloneOption, viewport } from '../../utils/utils';
import StaticLineTableProvider from './StaticLineTableStore/StaticLineTableProvider';
// US_312953
import UserLoggedPricesToggle from '../UserLoggedPricesToggle/UserLoggedPricesToggle';
import {  HOMESECURITYPAGETYPE, MOBILERATEPLAN, ONE_ECOMMERCE_OTT } from '../../utils/constants';
import Table from '../Table/table';

const runWhenIdle = (callback: () => void) => {
  const idleCallback = (globalThis as any)?.requestIdleCallback;

  if (typeof idleCallback === 'function') {
    idleCallback(callback, { timeout: 1000 });
    return;
  }

  setTimeout(callback, 0);
};

const StaticLineTable: FC<StaticLinetableSchema> = ({
  content,
  tabCheckCallBack,
  setGenericErrorModalDetails,
  metadata,
  loginSuccess,
  changeAddressClicked,
  linecheckSuccessCallBack,
  selectedOption,
  basicConfigUrl,
  selectedExistingOption,
  setLoginSuccess,
  lineCheckCallBack,
  deviceFlow,
  deviceFlowCallBack,
  ubRatePlanObj,
  lineCheckOnClear,
  pegaIntegration,
  useStoryblokOrder,
  pegaCustomerInterest,
  isProdEnv
}) => {
  const [index, setIndex] = useState(0);
  const [selectedTabId, setSelectedTabId] = useState<string>('');
  const [toggleState, setToggleState] = useState({
    TV: false,
    Netflix: false,
    gigaSpeed: false,
    gigaSpeedSelectionTitle: '',
    tabSet: false
  });
  const previousTvToggleRef = useRef<boolean>(false);
  const previousGigaSpeedToggleRef = useRef<boolean>(false);
  const constants = content?.oneEcomConstants?.[0];
  const tvSelectionTitle = 'Add TV';

  const viewPort = viewport();
  const [bundle, setBundle] = useState<any>(
    typeof window !== 'undefined' && localStorage.getItem('lineCheckResponse')
  );
  const [eligibleBundle, setEligibleBundle] = useState<any>(bundle && JSON?.parse(bundle));
  const [address, setAaddress] = useState<any>(
    typeof window !== 'undefined' && Cookies.get('chAddressJson')
  );
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const carouselMinHeightRef = useRef<number>(0);

  // US_312953
  const loggedInPriceConfig = content?.loggedInPricesConfig && content?.loggedInPricesConfig?.length > 0 ? 
    content?.loggedInPricesConfig?.find((item: any) => item?.enableLoggedInPrice === true) : null;
  
  /**
   * for getting the taboption from the url
   */
  useEffect(() => {
    if (!content?.noTabs) {
      const params = new URLSearchParams(window?.location?.search);
      const option = params?.get('tabOption');
      if (content && option !== null) {
        // Set both tabId and index when URL param exists
        setSelectedTabId(option);
        const foundIndex = (content?.productContainer || []).findIndex((product: any) => product?.tabId === option);
        if (foundIndex !== -1) {
          setIndex(foundIndex);
        }
      } else if (content?.productContainer?.[0]?.tabId && !selectedTabId) {
        // Set initial tabId if not set
        setSelectedTabId(content.productContainer[0].tabId);
      }
      const selectedTab = selectedTabId || content?.productContainer?.[index]?.tabId;
      if (typeof tabCheckCallBack === 'function') {
        tabCheckCallBack?.(selectedTab);
      }
    }
  }, [content]);

  /**
   * resetting the values when there is a change in linecheck
   */
  useEffect(() => {
    const latestBundle = typeof window !== 'undefined' && localStorage.getItem('lineCheckResponse');
    setBundle(latestBundle);
    setEligibleBundle(latestBundle && JSON?.parse(latestBundle));
    setAaddress(typeof window !== 'undefined' && Cookies.get('chAddressJson'));
  }, [changeAddressClicked]);

  /* to switch between young rateplans and normal ratePlans */
  useEffect(() => {
    if (deviceFlow?.youngToggle) {
      setIndex(1);
    } else if (deviceFlow?.isDeviceFlow) {
      setIndex(0);
    }
  }, [deviceFlow?.youngToggle]);

  //for no tabs and taboption
  useEffect(() => {
    if (content?.noTabs) {
      const params = new URLSearchParams(window?.location?.search);
      const option = params?.get('tabOption');
      if (option !== null && !toggleState?.tabSet) {
        if (content?.intTvTabId === option) {
          setToggleState({ ...toggleState, TV: true, tabSet: true });
        } else if (content?.intStreamTabId === option) {
          setToggleState({ ...toggleState, TV: true, Netflix: true, tabSet: true });
        } else if (content?.intTabId === option) {
          setToggleState({ ...toggleState, TV: false, Netflix: false, tabSet: true });
        } else {
          setToggleState({ ...toggleState, tabSet: true });
        }
      }
      setIndex(0);
    }
  }, [toggleState]);

  useEffect(() => {
    if (!previousTvToggleRef.current && toggleState?.TV) {
      runWhenIdle(() => {
        trackNewDatalayerEvent(GAEVENT, TOGGLECLICK, {
          click_text: true,
          selection: tvSelectionTitle,
          component: 'line-table'
        });
      });
    }

    if (previousTvToggleRef.current && !toggleState?.TV) {
      runWhenIdle(() => {
        trackNewDatalayerEvent(GAEVENT, TOGGLECLICK, {
          click_text: false,
          selection: tvSelectionTitle,
          component: 'line-table'
        });
      });
    }

    previousTvToggleRef.current = !!toggleState?.TV;
  }, [toggleState?.TV, tvSelectionTitle]);

  useEffect(() => {
    if (!toggleState?.gigaSpeedSelectionTitle) {
      previousGigaSpeedToggleRef.current = !!toggleState?.gigaSpeed;
      return;
    }

    if (!previousGigaSpeedToggleRef.current && toggleState?.gigaSpeed) {
      runWhenIdle(() => {
        trackNewDatalayerEvent(GAEVENT, TOGGLECLICK, {
          click_text: true,
          selection: toggleState?.gigaSpeedSelectionTitle,
          component: 'line-table'
        });
      });
    }

    if (previousGigaSpeedToggleRef.current && !toggleState?.gigaSpeed) {
      runWhenIdle(() => {
        trackNewDatalayerEvent(GAEVENT, TOGGLECLICK, {
          click_text: false,
          selection: toggleState?.gigaSpeedSelectionTitle,
          component: 'line-table'
        });
      });
    }

    previousGigaSpeedToggleRef.current = !!toggleState?.gigaSpeed;
  }, [toggleState?.gigaSpeed, toggleState?.gigaSpeedSelectionTitle]);

  const linecheckSuccessCallBackLocal = () => {
    setBundle(typeof window !== 'undefined' && localStorage.getItem('lineCheckResponse'));

    typeof linecheckSuccessCallBack == 'function' && linecheckSuccessCallBack();
  };

  const lineCheckOnClearLocal = () => {
    const latestBundle = typeof window !== 'undefined' && localStorage.getItem('lineCheckResponse');
    setBundle(latestBundle);
    setEligibleBundle(latestBundle && JSON?.parse(latestBundle));
    setAaddress(typeof window !== 'undefined' && Cookies.get('chAddressJson'));
    typeof lineCheckOnClear == 'function' && lineCheckOnClear();
  };
  useEffect(() => {
    setEligibleBundle(bundle && JSON?.parse(bundle));
    setAaddress(typeof window !== 'undefined' && Cookies.get('chAddressJson'));
  }, [bundle]);

  useEffect(() => {
    const carouselElement = carouselRef?.current;
    if (!carouselElement) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const carouselHeight = carouselElement.clientHeight;
      if (carouselHeight > carouselMinHeightRef.current) {
        carouselMinHeightRef.current = carouselHeight;
        carouselElement.style.minHeight = `${carouselHeight}px`;
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [index, carouselRef, isFetching]);

  useLayoutEffect(() => {
    const ele = document.getElementById('linecheck');
    if (ele && content?.productContainer?.[index]) {
      const currentTabId = content?.productContainer[index].pageType;
      // Hide linecheck for MOBILE and HOMESECURITY tabs without causing layout shift
      if (currentTabId === MOBILERATEPLAN || currentTabId === HOMESECURITYPAGETYPE) {
        ele.style.visibility = 'hidden';
        ele.style.pointerEvents = 'none';
      } else {
        ele.style.visibility = 'visible';
        ele.style.pointerEvents = 'auto';
      }
    }
  }, [index, content])

  const ottCartData = typeof window !== 'undefined' ? getSessionStorage(ONE_ECOMMERCE_OTT) : null;
  const isStandaloneOptionCheck = ottCartData?.products && Array.isArray(ottCartData.products) && ottCartData.products.length > 0 
    ? isStandaloneOption(ottCartData.products) 
    : false;
  // Auto-switch to Internet & TV tab when standalone option is detected
  useEffect(() => {
    if (isStandaloneOptionCheck && content?.productContainer) {
      const internetTvTab = content.productContainer.find((product: any) => {
        return product?.pageType === BUNDLEPRODUCT
      });
      if (internetTvTab?.tabId) {
        setSelectedTabId(internetTvTab.tabId);
        const tabIndex = content.productContainer.findIndex((p: any) => p.tabId === internetTvTab.tabId);
        if (tabIndex !== -1) {
          setIndex(tabIndex);
        }
      }
    }
  }, [isStandaloneOptionCheck, content, selectedTabId]);
  return (
    <StaticLineTableProvider>
      <div>
        <div id='static-linetable'>
          {viewPort && content?.productContainer && (
            <>
              {!content?.noTabs && !isStandaloneOptionCheck && (
                <Row>
                  <div className={`row  justify-content-center ${Styles.tabblock}`}>
                    {content?.productContainer?.length > 1 && (
                      <div className={` ${Styles.tabcontainer}`}>
                        {(content?.productContainer || []).map((product: any, i: number) => {
                          // Use tabId for comparison, fallback to index if tabId not available
                          const isActive = selectedTabId 
                            ? selectedTabId === product?.tabId 
                            : index === i;
                            
                          return (
                            <div
                              className={` ${Styles.tabs} ${
                                isActive ? Styles.tabcontentactive : Styles.tabcontentinactive
                              }`}
                              key={product?.tabId || i}
                            >
                              <label className={` ${Styles.tabcontent} `}>
                                <input
                                  type='radio'
                                  checked={isActive}
                                  onChange={() => {
                                    setIndex(i);
                                    setSelectedTabId(product?.tabId);
                                    typeof tabCheckCallBack === 'function' &&
                                      tabCheckCallBack?.(product?.tabId);
                                    trackNewDatalayerEvent(GAEVENT, TABCLICK, {
                                      click_text: product?.tabCaption
                                    });
                                  }}
                                />
                                {product?.tabCaption}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Row>
              )}
              {/* US_312953 */}
              {loggedInPriceConfig?.enableLoggedInPrice && loggedInPriceConfig.togglePosition === 'TOP' && (
                <UserLoggedPricesToggle
                  content={content}
                  index={0}
                  lineTable={false}
                  toggleContent={loggedInPriceConfig}
                />)
              }
              {/* END US_312953 */}
              <Row resuablelineTable={true}>
                <div className={`position-relative ${Styles.carouselWrapper}`} ref={carouselRef}>
                  <Carousel
                    activeIndex={index}
                    indicators={false}
                    controls={false}
                    interval={null}
                    id='pt-carousel'
                    slide={false}
                  >
                    {content?.productContainer?.length > 0 &&
                      (content?.productContainer || []).map((product: any, i: number) => {
                        return (
                          <Carousel.Item key={product?.tabId || i}>
                            {product.pageType === HOMESECURITYPAGETYPE ? (
                              <div className='mt-4'>
                                <Table content={product?.productList?.[0]} />
                              </div>
                            )
                            :
                            (
                              <StaticProductContainer
                                content={product}
                                setGenericErrorModalDetails={setGenericErrorModalDetails}
                                metadata={metadata}
                                tabIndex={i}
                                constants={constants}
                                loginSuccess={loginSuccess}
                                activeIndex={index}
                                mainContent={content}
                                address={address}
                                eligibleBundle={eligibleBundle}
                                linecheckSuccessCallBack={linecheckSuccessCallBackLocal}
                                selectedOption={selectedOption}
                                basicConfigUrl={basicConfigUrl}
                                selectedExistingOption={selectedExistingOption}
                                setLoginSuccess={setLoginSuccess}
                                lineCheckCallBack={lineCheckCallBack}
                                setToggleState={setToggleState}
                                toggleState={toggleState}
                                noTabs={content?.noTabs}
                                deviceFlow={deviceFlow}
                                deviceFlowCallBack={deviceFlowCallBack}
                                ubRatePlanObj={ubRatePlanObj}
                                lineCheckOnClear={lineCheckOnClearLocal}
                                pegaIntegration={pegaIntegration}
                                useStoryblokOrder={useStoryblokOrder}
                                pegaCustomerInterest={pegaCustomerInterest}
                                isFetching={isFetching}
                                setIsFetching={setIsFetching}
                                isProdEnv={isProdEnv}
                                toggleContent={loggedInPriceConfig}
                                localCacheDuration={content?.localCacheDuration}
                              />
                            )}
                          </Carousel.Item>
                        );
                      })}
                  </Carousel>
                </div>
              </Row>
              {/* US_312953 */}
              {loggedInPriceConfig?.enableLoggedInPrice && loggedInPriceConfig.togglePosition === 'BOTTOM' && (
                <UserLoggedPricesToggle
                  content={content}
                  index={0}
                  lineTable={false}
                  toggleContent={loggedInPriceConfig}
                />)
              }
              {/* END US_312953 */}
            </>
          )}
        </div>
      </div>
    </StaticLineTableProvider>
  );
};

export default StaticLineTable;
